/**
 * FieldPropertiesPanel Component
 *
 * Properties panel for editing form field attributes.
 * Shows when a field is selected in the PDF template editor.
 */

import React from 'react';

// Customer fields available for data mapping
const CUSTOMER_FIELDS = [
  { value: '', label: '-- No mapping --' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: "first_name + ' ' + last_name", label: 'Full Name' },
  { value: 'dob', label: 'Date of Birth' },
  { value: 'ssn', label: 'SSN' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'street_address', label: 'Street Address' },
  { value: 'address_line2', label: 'Address Line 2' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip_code', label: 'ZIP Code' },
  { value: 'county', label: 'County' },
  { value: 'fl_blue_member_id', label: 'FL Blue Member ID' },
  { value: 'smoker_status', label: 'Smoker Status' },
  { value: 'referral_source', label: 'Referral Source' },
];

// Agent fields available for data mapping
const AGENT_FIELDS = [
  { value: 'agent.full_name', label: 'Full Name' },
  { value: 'agent.first_name', label: 'First Name' },
  { value: 'agent.last_name', label: 'Last Name' },
  { value: 'agent.npn', label: 'NPN' },
  { value: 'agent.license_number', label: 'License Number' },
  { value: 'agent.agency_name', label: 'Agency Name' },
  { value: 'agent.phone', label: 'Phone' },
  { value: 'agent.work_email', label: 'Work Email' },
  { value: 'agent.email', label: 'Login Email' },
  { value: 'agent.street_address', label: 'Street Address' },
  { value: 'agent.city', label: 'City' },
  { value: 'agent.state', label: 'State' },
  { value: 'agent.zip_code', label: 'ZIP Code' },
];

// Signature role options
const SIGNATURE_ROLES = [
  { value: 'client', label: 'Client/Primary' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'agent', label: 'Agent' },
  { value: 'witness', label: 'Witness' },
];

export default function FieldPropertiesPanel({ field, onUpdate, onDelete }) {
  if (!field) {
    return (
      <div className="p-4 text-gray-500 text-sm">
        <p className="font-semibold mb-2">Field Properties</p>
        <p>Select a field to edit its properties, or drag a new field from the toolbox.</p>
      </div>
    );
  }

  const handleChange = (key, value) => {
    onUpdate({ ...field, [key]: value });
  };

  const handleNumberChange = (key, value) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0) {
      onUpdate({ ...field, [key]: num });
    }
  };

  return (
    <div className="p-4 overflow-y-auto h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold">Field Properties</h3>
        <span
          className={`text-xs px-2 py-1 rounded text-white ${
            field.type === 'text'
              ? 'bg-blue-500'
              : field.type === 'date'
              ? 'bg-green-500'
              : field.type === 'checkbox'
              ? 'bg-yellow-500'
              : 'bg-purple-500'
          }`}
        >
          {field.type}
        </span>
      </div>

      <div className="space-y-4">
        {/* Field Name */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Field Name (internal)
          </label>
          <input
            type="text"
            value={field.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="e.g., primary_name"
          />
        </div>

        {/* Display Label */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Display Label
          </label>
          <input
            type="text"
            value={field.label || ''}
            onChange={(e) => handleChange('label', e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="e.g., Primary Insured Name"
          />
        </div>

        {/* Data Mapping (for non-signature fields) */}
        {field.type !== 'signature' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Auto-fill Data Mapping
            </label>
            <select
              value={field.dataMapping || ''}
              onChange={(e) => handleChange('dataMapping', e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              <option value="">-- No mapping --</option>
              <optgroup label="Customer Fields">
                {CUSTOMER_FIELDS.filter(cf => cf.value).map((cf) => (
                  <option key={cf.value} value={cf.value}>
                    {cf.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Agent Fields">
                {AGENT_FIELDS.map((af) => (
                  <option key={af.value} value={af.value}>
                    {af.label}
                  </option>
                ))}
              </optgroup>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Field will be pre-filled with customer or agent data
            </p>
          </div>
        )}

        {/* Signature Role (for signature fields) */}
        {field.type === 'signature' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Signature Role
            </label>
            <select
              value={field.signatureRole || 'client'}
              onChange={(e) => handleChange('signatureRole', e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              {SIGNATURE_ROLES.map((sr) => (
                <option key={sr.value} value={sr.value}>
                  {sr.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Who should sign this field
            </p>
          </div>
        )}

        {/* Required */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="required"
            checked={field.required || false}
            onChange={(e) => handleChange('required', e.target.checked)}
            className="rounded"
          />
          <label htmlFor="required" className="text-sm text-gray-700">
            Required field
          </label>
        </div>

        {/* Position */}
        <div className="border-t pt-4">
          <h4 className="text-xs font-medium text-gray-700 mb-2">Position (pixels)</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500">X</label>
              <input
                type="number"
                value={field.x || 0}
                onChange={(e) => handleNumberChange('x', e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">Y</label>
              <input
                type="number"
                value={field.y || 0}
                onChange={(e) => handleNumberChange('y', e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Size */}
        <div>
          <h4 className="text-xs font-medium text-gray-700 mb-2">Size (pixels)</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500">Width</label>
              <input
                type="number"
                value={field.width || 100}
                onChange={(e) => handleNumberChange('width', e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
                min="10"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">Height</label>
              <input
                type="number"
                value={field.height || 24}
                onChange={(e) => handleNumberChange('height', e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
                min="10"
              />
            </div>
          </div>
        </div>

        {/* Font Size (for text/date fields) */}
        {(field.type === 'text' || field.type === 'date') && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Font Size
            </label>
            <input
              type="number"
              value={field.fontSize || 10}
              onChange={(e) => handleNumberChange('fontSize', e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
              min="6"
              max="24"
            />
          </div>
        )}

        {/* Field ID (read-only) */}
        <div className="border-t pt-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">Field ID</label>
          <input
            type="text"
            value={field.id || ''}
            readOnly
            className="w-full border rounded px-2 py-1 text-xs bg-gray-50 text-gray-500"
          />
        </div>

        {/* Delete Button */}
        <div className="border-t pt-4">
          <button
            onClick={() => {
              if (window.confirm('Delete this field?')) {
                onDelete(field.id);
              }
            }}
            className="w-full bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 text-sm"
          >
            Delete Field
          </button>
        </div>
      </div>
    </div>
  );
}
